import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, List, fromJS } from 'immutable';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import EventViewActions from 'flux/actions/EventViewActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import GlobalElementActions from 'flux/actions/GlobalElementActions';
import PrintableCuesheetActions from 'flux/actions/cuesheet.printable/PrintableCuesheetActions';
import PDFExportFormStore from 'flux/stores/cuesheet.printable/PDFExportFormStore';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import CuesheetDataHelper from 'flux/stores/cuesheet/CuesheetDataHelper';
import { DisplayTypes } from 'shared/constants/FlowConstants';
import AlertTypes from 'shared/constants/AlertTypes';
import AlertActions from 'flux/actions/AlertActions';
import ModalActions from 'flux/actions/ModalActions';
import Messages from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import { PageFormats, RowSizes } from 'shared/constants/Export';
import {
  EditableCellTypes,
  ColumnWidths,
  ItemColumnSegmentWidths,
} from 'flux/stores/cuesheet/CuesheetConstants';
import UserActions from 'flux/actions/UserActions';
import qs from 'query-string';

const loadFromSessionCompleted = UserActions.loadFromSession.completed;

const defaultSpecialColumnOrder = [
  EditableCellTypes.ItemNumber,
  EditableCellTypes.ItemStartTime,
  EditableCellTypes.ItemPresetDuration,
  EditableCellTypes.ItemDuration,
  EditableCellTypes.ItemTitle,
  EditableCellTypes.ItemDescription,
  EditableCellTypes.ItemNote,
];

export default Reflux.createStore({
  listenables: [PrintableCuesheetActions],

  mixins: [BaseMixin],

  init() {
    this.setState(this._getInitState());

    this.listenTo(
      GlobalElementActions.populateGlobalElementTags.completed,
      'onPopulateGlobalElementTagsCompleted',
    );
    this.listenTo(EventViewActions.populate.failed, 'onEventPopulateFailed');
    this.listenTo(
      EventViewActions.populate.completed,
      'onEventPopulateCompleted',
    );
    this.listenTo(
      TeamViewActions.populate.completed,
      'onTeamPopulateCompleted',
    );
    this.listenTo(CuesheetActions.populate, 'onCuesheetPopulate');
    this.listenTo(
      CuesheetActions.populate.completed,
      'onCuesheetPopulateCompleted',
    );
    this.listenTo(CuesheetActions.populate.failed, 'onCuesheetPopulateFailed');
    this.listenTo(loadFromSessionCompleted, 'onUserLoadFromSessionCompleted');

    //Guest Pass
    this.listenTo(
      EventViewActions.populateForGuest.failed,
      'onEventPopulateFailed',
    );
    this.listenTo(
      EventViewActions.populateForGuest.completed,
      'onEventPopulateCompleted',
    );
    this.listenTo(
      TeamViewActions.populateForGuest.completed,
      'onTeamPopulateCompleted',
    );
    this.listenTo(CuesheetActions.populateForGuest, 'onCuesheetPopulate');
    this.listenTo(
      CuesheetActions.populateForGuest.completed,
      'onCuesheetPopulateCompleted',
    );
    this.listenTo(
      CuesheetActions.populateForGuest.failed,
      'onCuesheetPopulateFailed',
    );

    //PDF Export Form Store
    this.listenTo(PDFExportFormStore, this.onPDFExportFormStoreChange);
    this.listenTo(CuesheetStore, this.onCuesheetStoreChange);
  },

  _getInitState() {
    return Map({
      customPageBreaks: false,
      displayTeamName: false,
      displayTeamLogo: false,
      displayEventName: false,
      displayEventLogo: false,
      displayShowName: false,
      landscape: true,
      format: PageFormats.Letter,
      generic: true,
      grayscale: false,
      guest: false,
      pageBreaks: [],
      rowSize: RowSizes.Compact,
      loading: true,
    });
  },

  onClear() {
    this.setStateAndTrigger(this._getInitState());
  },

  /**
   * Is the printable cuesheet rendering for guest pass?
   */
  onSetGuest(guestPass) {
    this.setInState('guest', guestPass);
  },

  onUserLoadFromSessionCompleted(user) {
    this.setInState('userId', user._id);
    this.setInState('user', Map(user));

    this._userLoadCallback();
  },

  /**
   * Helper function to only load data when user object is in state
   */
  _userLoadCallback() {
    if (this._populateDataCallback) this._populateDataCallback();
  },

  onEventPopulateFailed(err) {
    /**
     * If event fails to populate with a 404 it is likely a deleted event.
     * Throw modal error for deleted events.
     */
    if (err.status === 404) {
      ModalActions.show(UIRegions.ViewingDeletedEventError);
    }
  },

  onEventPopulateCompleted(event) {
    this.setInStateAndTrigger('event', Map(event));

    if (event.globalElementManager) {
      GlobalElementActions.populateGlobalElementTags(event.team, event._id);
    }
  },

  onPopulateGlobalElementTagsCompleted(tags) {
    let allTags = OrderedMap();

    tags.forEach((tag) => {
      if (tag.deleted) return false;
      allTags = allTags.set(tag._id, fromJS(tag));
    });

    this.setInStateAndTrigger('tags', allTags);
  },

  onTeamPopulateCompleted(team) {
    this.setInStateAndTrigger('team', Map(team));
  },

  onCuesheetStoreChange(cueState) {
    if (
      !cueState.get('rowMap') ||
      !cueState.get('rowMap') ||
      cueState.get('rowOrder')
    )
      return;
    let next = this.getState();

    if (
      cueState.get('rows') === next.get('rows') &&
      cueState.get('rowMap') === next.get('rowMap') &&
      cueState.get('rowOrder') === next.get('rowOrder')
    )
      return;
    next = next.withMutations((st) => {
      st.set('rows', cueState.get('rows'));
      st.set('rowMap', cueState.get('rowMap'));
      st.set('rowOrder', cueState.get('rowOrder'));

      return st;
    });

    this.setStateAndTrigger(next);
  },

  onPDFExportFormStoreChange(PDFExportState) {
    if (window.location.href.indexOf('custom') !== -1) return;

    let next = this.getState();

    const pdfExportDataColumns = PDFExportState.get('dataColumns');

    const pdfExportColumnOrder = PDFExportState.get('columnOrder');

    next = next
      .set('rowSize', PDFExportState.get('rowSize'))
      .set('grayscale', PDFExportState.get('grayscale'))
      .set('displayTeamTitle', PDFExportState.get('displayTeamTitle'))
      .set('displayTeamLogo', PDFExportState.get('displayTeamLogo'))
      .set('displayEventTitle', PDFExportState.get('displayEventTitle'))
      .set('displayEventLogo', PDFExportState.get('displayEventLogo'))
      .set('displayShowTitle', PDFExportState.get('displayShowTitle'))
      .set(
        'includePrivateNoteContents',
        PDFExportState.get('includePrivateNoteContents'),
      );

    if (PDFExportState.has('settings')) {
      next = next.set('settings', PDFExportState.get('settings'));
    }

    if (pdfExportDataColumns && pdfExportColumnOrder) {
      next = this._setExportColumnState(
        next,
        pdfExportDataColumns,
        pdfExportColumnOrder,
      );
    }

    this.setStateAndTrigger(next);
  },

  onCuesheetPopulate(teamId, eventId, showId) {
    this.setInState('teamId', teamId);
    this.setInState('eventId', eventId);
    this.setInState('showId', showId);
  },

  onCuesheetPopulateFailed(err) {
    if (!this.getInState('guest')) return;

    if (err.status === 404)
      AlertActions.show(
        Messages.guestPassDisabled,
        null,
        AlertTypes.nondismissableAlert,
      );
  },

  onCuesheetPopulateCompleted(result) {
    const { itemSetList, show, globalElementShowItems } = result;

    if (show.archived && this.getInState('guest')) {
      return AlertActions.show(
        Messages.guestPassDisabled,
        null,
        AlertTypes.nondismissableAlert,
      );
    }

    if (!this.getInState('user') && !this.getInState('guest')) {
      this._populateDataCallback = () => {
        this._populateShowData(show);
        this._populateItemSetData(itemSetList, globalElementShowItems);
      };

      return;
    }

    this._populateShowData(show);
    this._populateItemSetData(itemSetList, globalElementShowItems);
  },

  _populateShowData(show) {
    this.setInStateAndTrigger('show', Map(show));
  },

  _populateItemSetData(itemSetList, globalElementShowItems) {
    //TODO: we need to try to consolidate this w. a helper or mixin...this is basically the same as in CuesheetStore

    const userId = this.getInState('userId');
    this.setInState('itemSetListId', itemSetList._id);
    const itemSet = itemSetList.itemSets[0];
    this.setInState('itemSetId', itemSet._id);

    const { rowOrder } = itemSet;

    const settings =
      Object.keys(itemSetList.settings).length > 0
        ? fromJS(itemSetList.settings)
        : fromJS({ itemColumn: { width: 'narrow' } });

    const userSettings = fromJS(
      itemSetList.userSettings[userId] || { noteColumn: { width: 'narrow' } },
    );

    let next = this.getState()
      .set('settings', settings)
      .set('userSettings', userSettings);

    //TODO: clean this up...move it to the data helper
    next = next.withMutations((st) => {
      const rowMap = CuesheetDataHelper.createNestedMap(
        itemSetList,
        globalElementShowItems,
      );
      const columnOrderPersonal = CuesheetDataHelper.getColumnOrder(
        itemSetList,
        next.get('userId'),
      );
      const columnOrderGeneric =
        CuesheetDataHelper.getFullColumnOrder(itemSetList);
      const columnOrderGuest =
        CuesheetDataHelper.getGuestColumnOrder(itemSetList);

      const exportColumnOrder = List(
        defaultSpecialColumnOrder.concat(itemSetList.defaultColumnOrder),
      );
      st.set('exportColumnOrder', exportColumnOrder);

      //set the row index data
      st.set('rows', CuesheetDataHelper.createRowIndex(itemSetList, rowMap));
      //set the row map data
      st.set('rowMap', rowMap);
      st.set('rowOrder', rowOrder);

      //set the column data
      this.setColumnState(columnOrderPersonal, itemSetList.columns, st);
      this.setPersonalColumnState(columnOrderPersonal, itemSetList.columns, st);
      this.setGenericColumnState(columnOrderGeneric, itemSetList.columns, st);
      this._setGuestColumnState(columnOrderGuest, itemSetList.columns, st);

      this.setCustomColumnState(itemSetList.columns, st);
      this.setCustomPageBreakState(st);
      this._setUrlParamsState(st);

      /*
      To maintain the order of your items (i.e. array as an ordered list), you
      really only have one option, which is passing a delimited list of values
      and explicitly converting it to an array.
      */

      return st;
    });

    next = CuesheetDataHelper.getStateWithDerivedCalculations(
      next,
      this.getInState(['show', 'autoTimeCalculation']),
      this.getInState(['show', 'startOn']),
    );

    next = next.set('loading', false);

    this.setStateAndTrigger(next);
  },

  /**
   * Set the column state, which includes the columnOrder (only visible), the column list (only the visible ones) and the
   * list of selectable cells
   */
  setColumnState(columnOrder, columns, state) {
    const next = state || this.getState();

    const dataColumns = CuesheetDataHelper.getSortedColumns(
      columns,
      columnOrder,
    );

    return next.set('columns', dataColumns);
  },

  _getSpecialColumns(state, pdfExportColumnOrder) {
    const next = state || this.getState();

    return CuesheetDataHelper.getSpecialColumnsForGuestAndPrintable(
      next,
      pdfExportColumnOrder,
    );
  },

  /**
   * Get sorted data cell columns
   */
  _getDataColumns(columns, columnOrder) {
    return CuesheetDataHelper.getSortedColumns(columns, columnOrder);
  },

  /**
   * Set Personal User Column Order
   *
   * @param columnOrder //User's personal column order. Defaults to default
   * column order if user does not have a custom column order.
   * @param columns //List of column objects for show
   * @param state
   *
   */
  setPersonalColumnState(columnOrder, columns, state) {
    let next = state || this.getState();

    const userId = state.get('userId');

    const dataColumns = this._getDataColumns(columns, columnOrder);
    let specialColumns = this._getSpecialColumns(next);

    if (this.getInState(['userSettings', 'noteColumn', 'hidden'])) {
      specialColumns = specialColumns.delete(EditableCellTypes.ItemNote);
    }

    const columnsPersonal = specialColumns
      .concat(dataColumns)
      .filter((column) => {
        const userSettings = column.get('userSettings');
        const hidden =
          userSettings && userSettings[userId]
            ? userSettings[userId].hidden
            : false;

        // If there are no user settings or user setting "hidden" is false, add column to columns list
        return !hidden;
      });

    const columnsPersonalWidth = this._getTableWidth(columnsPersonal);

    next = next
      .set('columnsPersonal', columnsPersonal)
      .set('columnsPersonalWidth', columnsPersonalWidth);

    return next;
  },

  setCustomColumnState(columns, state) {
    let next = state || this.getState();

    const { columnOrder } = qs.parse(window.location.search, {
      arrayFormat: 'index',
    });

    if (!columnOrder) {
      next = next
        .set('columnsCustom', next.get('columnsGeneric'))
        .set('columnsCustomWidth', next.get('columnsGenericWidth'));
      return next;
    }

    //This returns data columns with empty special columns
    const dataColumns = this._getDataColumns(columns, columnOrder);
    const specialColumns = this._getSpecialColumns(next);

    const columnMap = dataColumns.concat(specialColumns);

    let activeColumns = OrderedMap();

    columnOrder.forEach((columnId) => {
      columnId = EditableCellTypes[columnId] || columnId;
      activeColumns = activeColumns.set(columnId, columnMap.get(columnId));
    });

    const columnsCustomWidth = this._getTableWidth(activeColumns);

    next = next
      .set('columnsCustom', activeColumns)
      .set('columnsCustomWidth', columnsCustomWidth);

    return next;
  },

  setCustomPageBreakState(state) {
    let next = state || this.getState();

    const { customPageBreaks } = qs.parse(window.location.search, {
      arrayFormat: 'index',
    });

    if (!customPageBreaks) return;

    next = next
      .set('customPageBreaks', true)
      .set('pageBreaks', customPageBreaks);

    return next;
  },

  _setUrlParamsState(next) {
    const { cssZoom, grayscale, rowSize, includePrivateNoteContents } =
      qs.parse(window.location.search);

    //if zoom, use css zoom for legacy printable view
    if (cssZoom) next = next.set('cssZoom', cssZoom);
    if (grayscale) next = next.set('grayscale', grayscale === 'true');
    if (rowSize) next = next.set('rowSize', rowSize);
    if (includePrivateNoteContents)
      next = next.set('includePrivateNoteContents', includePrivateNoteContents);

    return next;
  },

  _setExportColumnState(next, pdfExportDataColumns, pdfExportColumnOrder) {
    next = next || this.getState();

    const specialColumns = this._getSpecialColumns(next, pdfExportColumnOrder);
    let dataColumns = OrderedMap();

    pdfExportColumnOrder.forEach((colId) => {
      if (pdfExportDataColumns.has(colId)) {
        dataColumns = dataColumns.set(colId, pdfExportDataColumns.get(colId));
      }
    });

    const exportColumns = specialColumns.concat(dataColumns);
    const exportColumnsWidth = this._getTableWidth(exportColumns);

    next = next
      .set('columnsExport', exportColumns)
      .set('columnsExportWidth', exportColumnsWidth);

    return next;
  },

  /**
   * Set Generic Column Order
   *
   * @param columnOrder //Default column order
   * @param columns //List of column objects for show
   * @param state
   *
   */
  setGenericColumnState(columnOrder, columns, state) {
    let next = state || this.getState();

    const dataColumns = this._getDataColumns(columns, columnOrder);
    const specialColumns = this._getSpecialColumns(state);

    const genericSpecialColumns = specialColumns
      .concat(dataColumns)
      .delete(EditableCellTypes.ItemNote);

    const columnsGenericWidth = this._getTableWidth(genericSpecialColumns);

    next = state
      .set('columnsGeneric', genericSpecialColumns)
      .set('columnsGenericWidth', columnsGenericWidth);

    return next;
  },

  /**
   * Set Guest Pass Column Order
   *
   * @param columnOrder //Default column order
   * @param columns //List of column objects for show
   * @param state
   *
   */
  _setGuestColumnState(columnOrder, columns, state) {
    let next = state || this.getState();

    const dataColumns = this._getDataColumns(columns, columnOrder);
    const allColumns = this._getSpecialColumns(state).concat(dataColumns);

    const columnsGuest = allColumns.delete(EditableCellTypes.ItemNote);

    const columnsGuestWidth = this._getTableWidth(columnsGuest);

    next = state
      .set('columnsGuest', columnsGuest)
      .set('columnsGuestWidth', columnsGuestWidth);

    return next;
  },

  _getTableWidth(columns) {
    let tableWidth = 0;

    const displayType = this.getInState(['show', 'displayType']);

    columns.forEach((column, columnId) => {
      function capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
      }

      if (columnId === EditableCellTypes.ItemNumber) {
        tableWidth += 40;
      }

      if (columnId === EditableCellTypes.ItemStartTime) {
        tableWidth += 85;
      }

      if (columnId === EditableCellTypes.ItemPresetDuration) {
        tableWidth += 90;
      }

      if (columnId === EditableCellTypes.ItemDuration) {
        tableWidth += 85;
      }

      if (column.get('width')) {
        let columnWidth = column.get('width');
        columnWidth = capitalize(columnWidth);

        if (
          columnId === EditableCellTypes.ItemTitle ||
          columnId === EditableCellTypes.ItemDescription
        ) {
          const width =
            displayType === DisplayTypes.show
              ? ItemColumnSegmentWidths[columnWidth]
              : ColumnWidths[columnWidth];
          tableWidth += width;
        } else {
          tableWidth += ColumnWidths[columnWidth];
        }
      }
    });

    return tableWidth;
  },
});
